//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import TransactionsModel from './transactions.model';

/**
 * @service TransactionsService
 * @description Service for managing financial and material transactions within the Material Management System (MMS).
 * This service is the backbone for tracking costs, inventory movements, and financial records associated with farming operations.
 *
 * It interacts with the `/mms/transactions/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Transaction Creation**: Recording new purchases, consumption, transfers, or adjustments.
 * 2. **Reporting**: Generating transaction registers and spend analysis reports.
 * 3. **Inventory Tracking**: Checking on-hand quantities and unit costs.
 * 4. **Role-Based Security**: Ensuring that non-employee users (e.g., Farmers) cannot access or modify data outside their business context by sanitizing payloads.
 * 5. **Resource Management**: Handling HTTP request lifecycles.
 *
 * **Data Flow:**
 * - **Create**: Component sends array of `TransactionsModel` -> Service posts to root endpoint.
 * - **Register**: Component requests register -> Service checks role -> Service posts to `transactionRegister`.
 * - **Analysis**: Various methods fetch aggregated spend data for charts and popups.
 *
 * @feature 1. Create multiple transactions in a batch.
 * @feature 2. Retrieve comprehensive transaction registers.
 * @feature 3. Analyze spend at Farm and Pond levels.
 * @feature 4. Check real-time inventory levels.
 *
 * @architecture
 * Extends base Service<TransactionsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/transactions/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE'. If not, `business_id` is removed from payloads to enforce context security.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionsService extends Service<TransactionsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/transactions/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/transactions/');
  }

  /**
   * @method createTransaction
   * @async
   * @description Create one or more new transactions.
   *
   * Sends a batch of transaction records to the backend for processing.
   *
   * @param {TransactionsModel[]} transactionData - Array of transaction models to be created.
   * @returns {Promise<Array<TransactionsModel>>} Promise resolving to the created transactions.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public createTransaction(transactionData: TransactionsModel[]) {
    return new Promise<Array<TransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<TransactionsModel>>(`${this.baseUrl}${this.serviceUrl}`, transactionData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTransactionRegister
   * @async
   * @description Retrieve the transaction register based on filter criteria.
   *
   * **Security Logic:**
   * - Checks `localStorage.loginPersonType`.
   * - If the user is **NOT** an 'EMPLOYEE', the `business_id` property is removed from the payload
   *   to prevent unauthorized access to other businesses' data.
   *
   * @param {any} transactionData - Filter object containing criteria like date range, farm site, etc.
   * @returns {Promise<Array<TransactionsModel>>} Promise resolving to the list of transactions.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Modifies `transactionData` (removes `business_id`) if user is not an employee.
   * - Initiates an HTTP POST request to `transactionRegister`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTransactionRegister(transactionData: any) {
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete transactionData.business_id;
    }
    return new Promise<Array<TransactionsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<TransactionsModel>>(`${this.baseUrl}${this.serviceUrl}transactionRegister`, transactionData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method onhandquantity
   * @async
   * @description Retrieve the latest on-hand quantity and unit cost for a specific item at a farm site.
   *
   * @param {string} farmsiteId - The unique identifier of the farm site.
   * @param {string} itemId - The unique identifier of the item.
   * @returns {Promise<TransactionsModel>} Promise resolving to the on-hand quantity details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `latestOnHand/unitCost/farmSiteId/itemId`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public onhandquantity(farmsiteId: string, itemId: string) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}latestOnHand/unitCost/farmSiteId/itemId`, {
        farmSiteId: farmsiteId,
        itemId: itemId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondTransactions
   * @async
   * @description Fetch transactions associated with a specific pond and culture cycle.
   *
   * **Security Logic:**
   * - Removes `business_id` from payload if user is not an 'EMPLOYEE'.
   *
   * @param {string} business_id - The business ID.
   * @param {string} farm_site_id - The farm site ID.
   * @param {number} pond_id - The pond ID.
   * @param {number} pond_culture_id - The culture cycle ID.
   * @param {string} transactionDate - The date of the transaction.
   * @returns {Promise<TransactionsModel>} Promise resolving to the transaction details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondTrasaction`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getPondTransactions(business_id: string, farm_site_id: string, pond_id: number, pond_culture_id: number, transactionDate: string) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      let payload = { business_id, farm_site_id, pond_id, pond_culture_id, transactionDate }
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete payload.business_id;
      }
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}pondTrasaction`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmSPendAnalysys
   * @async
   * @description Fetch farm spend analysis data for a given date range (likely for charts).
   *
   * **Security Logic:**
   * - Removes `business_id` from payload if user is not an 'EMPLOYEE'.
   *
   * @param {string} business_id - The business ID.
   * @param {string} farm_site_id - The farm site ID.
   * @param {string} chartFromdate - Start date for the analysis.
   * @param {string} chartTodate - End date for the analysis.
   * @returns {Promise<TransactionsModel>} Promise resolving to the analysis data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `farmSpendAnalysis`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getFarmSPendAnalysys(business_id: string, farm_site_id: string, chartFromdate: string, chartTodate: string) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      let payload = { business_id, farm_site_id, chartFromdate, chartTodate }
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete payload.business_id;
      }
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}farmSpendAnalysis`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmSpendAnalysis
   * @async
   * @description Fetch detailed farm spend analysis data.
   *
   * Similar to `getFarmSPendAnalysys` but uses standard date parameter names and includes default filters (`isBH: 0`, `production_unit_id: "null"`).
   *
   * **Security Logic:**
   * - Removes `business_id` from payload if user is not an 'EMPLOYEE'.
   *
   * @param {string} business_id - The business ID.
   * @param {string} farm_site_id - The farm site ID.
   * @param {string} from_date - Start date.
   * @param {string} to_date - End date.
   * @returns {Promise<TransactionsModel>} Promise resolving to the analysis data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `farmSpendAnalysis`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getFarmSpendAnalysis(business_id: string, farm_site_id: string, from_date: string, to_date: string) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      let payload = { business_id, farm_site_id, from_date, to_date, isBH: 0, production_unit_id: "null" }
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete payload.business_id;
      }
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}farmSpendAnalysis`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondSpendAnalysisPopup
   * @async
   * @description Fetch spend analysis data specifically for a pond popup view.
   *
   * **Security Logic:**
   * - Removes `business_id` from payload if user is not an 'EMPLOYEE'.
   *
   * @param {string} business_id - The business ID.
   * @param {string} farm_site_id - The farm site ID.
   * @param transactionDate
   * @returns {Promise<TransactionsModel>} Promise resolving to the pond spend data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `PondSpendAnalysis`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getPondSpendAnalysisPopup(business_id: string, farm_site_id: string, transactionDate: string) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      let payload = { business_id, farm_site_id, transactionDate }
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete payload.business_id;
      }
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}PondSpendAnalysis`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addFeelogTransaction
   * @async
   * @description Add an inventory transaction, often related to fee logs or specific inventory adjustments.
   *
   * @param {TransactionsModel} transaction - The transaction model to add.
   * @returns {Promise<TransactionsModel>} Promise resolving to the added transaction.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `addInventoryTxns`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public addFeelogTransaction(transaction: TransactionsModel) {
    return new Promise<TransactionsModel>((resolve, reject) => {
      const request = this.httpClient.post<TransactionsModel>(`${this.baseUrl}${this.serviceUrl}addInventoryTxns`, transaction, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
